import React from 'react'
import styled from 'styled-components'

import Card from 'components/Card'
import StatusButton, { StatusButtonProps } from 'pages/Dashboard/cards/shared/StatusButton'

import useScreenSize from 'hooks/useScreenSize'
import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

type Props = {
  title: string
  subTitle?: string
  icon?: React.ReactElement | null
  applyAccentColorToQuantity?: boolean
  rows: Array<{
    label: string
    buttons: Array<StatusButtonProps>
  }>
  isLoaded?: boolean,
  hehmEnabled?: boolean,
  activeHehmAccount?: boolean
}

const CardWithLabeledRowsOfButtons = ({
  title,
  subTitle = '',
  icon = null,
  applyAccentColorToQuantity = false,
  rows,
  isLoaded = true,
  hehmEnabled = false,
  activeHehmAccount = false
}: Props) => {
  const screenSize = useScreenSize()

  return (
    <S.StyledCard
      titleAreaLeft={hehmEnabled ? icon : null}
      title={title}
      titleAreaStyle={S.titleAreaStyle(activeHehmAccount)}
      isLoaded={isLoaded}
      hehmEnabled={hehmEnabled}
    >
      <S.Subtitle>
        {subTitle}
      </S.Subtitle>
      <S.Buttons>
        {rows.map((row) => (
          <div key={row.label}>
            <S.RowLabel $activeHehmAccount={activeHehmAccount}>{row.label}</S.RowLabel>
            <S.ButtonRow
              $isSmallScreen={screenSize === 'small' || !hehmEnabled}
              $hasMoreThanThree={row.buttons.length > 3}
            >
              {row.buttons.map((button) => (
                <StatusButton
                  {...button}
                  key={button.text}
                  applyAccentColorToQuantity={applyAccentColorToQuantity}
                  textStyle={S.buttonTextStyle}
                />
              ))}
            </S.ButtonRow>
          </div>
        ))}
      </S.Buttons>
    </S.StyledCard>
  )
}

export default CardWithLabeledRowsOfButtons

const S = {
  StyledCard: styled(Card).withConfig({
    shouldForwardProp: (prop) => !['hehmEnabled'].includes(prop)
  })<{hehmEnabled: boolean }>`
  ${({ hehmEnabled }) =>
    hehmEnabled &&
    `
      border-radius: 20px;
      border: 1px solid ${color('dashboardCardBorder')};
      font-family: 'Inter', regular;
    `}
  `,
  titleAreaStyle: (activeHehmAccount: boolean) => `
    align-items: center;
    h2 {
      margin-bottom: 0;
      line-height: 1.15;
      font-weight: 600;
      color: ${activeHehmAccount ? color('cardTitle') : color('inactiveCardTitle')};
    }
  `,
  Subtitle: styled.p`
    margin: 8px 0 16px;
    color: ${color('secondaryText')};
  `,
  Buttons: styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 17px;

    > *:not(:first-child) {
      margin-top: 10px;
    }
  `,
  ButtonRow: styled.div.withConfig({
    shouldForwardProp: (prop) =>
      !['$isSmallScreen', '$hasMoreThanThree'].includes(prop)
  })<{ $isSmallScreen: boolean; $hasMoreThanThree: boolean }>`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px;

    > * {
      flex: ${({ $isSmallScreen }) => $isSmallScreen ? '1 1 100%' : '0 0 calc(32% - 7px)'};
      max-width: ${({ $isSmallScreen }) => $isSmallScreen ? '100%' : 'calc(32% - 7px)'};
      min-width: ${({ $isSmallScreen }) => $isSmallScreen ? '0' : 'calc(32% - 7px)'};
    }
  `,
  RowLabel: styled.div.withConfig({
    shouldForwardProp: (prop) => !['$activeHehmAccount'].includes(prop)
  })<{ $activeHehmAccount?: boolean }>`
    margin-bottom: 4px;
    font-size: 12pt;
    color: ${({ $activeHehmAccount }) => $activeHehmAccount ? color('cardTitle') : color('inactiveCardText')};

    @media ${screenSize.medium} {
      font-size: 9pt;
    }

    @media ${screenSize.small} {
      font-size: 12pt;
    }
  `,
  buttonTextStyle: `
    color: ${color('primaryText')};
  `
}
